import { useRef } from "react";
import FsLightboxReact from "fslightbox-react";
import { FormattedDateTime } from "../FormattedDateTime";
import { selectors, setShowGallery } from "../../app/slices/mediaSlice";
import { connect } from "react-redux";

function FsLightbox(props) {
  const lightbox = useRef(null);
  const { media, galleryIndex, showGallery } = props;

  const { REACT_APP_IMAGEPATH } = process.env;
  const brokenImagePath = `${REACT_APP_IMAGEPATH}/broken-image.svg`;

  let captions = [];
  let mediaItems = [];
  let types = [];
  let customAttributes = [];
  let thumbs = [];

  // Iterate through the media, and create an array of captions, types and media items
  // Check that the media type is an array
  if (Array.isArray(media)) {
    media.filter(({ mediaTypeId }) => mediaTypeId !== 4).map((media) => {
      if (media.comment !== undefined) {
        mediaItems.push(media.url);
        updateTypesAndThumbsArray(types, thumbs, media.mediaTypeId, media.url)
        const customAttribute =
          media.description === null
            ? { alt: "User submitted Image" }
            : { alt: media.description };
        customAttributes.push(customAttribute);
        captions.push(createCaption(media.comment, media.caption));
      }
    })

    /**
     * Update the types and thumbs array for FSLightbox
     * @param {Array} types
     * @param {Array} thumbs
     * @param {Number} mediaTypeId
     * @param {String} url
     */
    function updateTypesAndThumbsArray(types, thumbs, mediaTypeId, url) {
      if (url === brokenImagePath) {
        types.push("null");
        thumbs.push(null);
      } else if (mediaTypeId === 1) {
        types.push("image");
        thumbs.push(url);
      } else if (mediaTypeId === 4) {
        types.push("youtube");
        // Get the YouTube thumbnail from the extracted YouTube ID.
        const splitUrl = url.split(/^.*(youtu.be\/|v\/|embed\/|watch\?|youtube.com\/user\/[^#]*#([^\/]*?\/)*)\??v?=?([^#\&\?]*).*/);
        const thumbnail = `https://img.youtube.com/vi/${splitUrl[3]}/mqdefault.jpg`;
        thumbs.push(thumbnail);
      }
    }

    /**
     * Create the caption for the media item
     * @param {String} comment
     * @returns
     */
    function createCaption(comment, caption) {
      const name = comment.memberId !== "" && comment.username !== "Anonymous User" ? comment.username : "Anonymous User";
      return {
        username: name,
        created: FormattedDateTime(comment.created),
        text: caption ?? comment.text
      };
    }
  }

  const handleTabbing = e => {
    const tabbableElements = [...lightbox?.current?.elements?.container.current?.querySelectorAll('[tabindex="0"]')];

    if (e?.key && (e.key === 'Tab') && tabbableElements.length > 0) {
      const firstFocusableElement = tabbableElements[0];
      const lastFocusableElement = tabbableElements[tabbableElements.length - 1];
      const moveFocusToFirstElement = e.target === lastFocusableElement && !e.shiftKey;
      const moveFocusToLastElement = e.target === firstFocusableElement && e.shiftKey;

      if (moveFocusToFirstElement || moveFocusToLastElement) {
        e.preventDefault();
        const nextFocusableElement = moveFocusToFirstElement ? firstFocusableElement : lastFocusableElement;
        nextFocusableElement.focus();
      }
    }
  };

  const trapFocusInLightbox = lightbox => {
    const addKeyHandlers = e => {
      window.removeEventListener('keyup', addKeyHandlers);
      if (e?.key && (e?.key === 'Enter' || e?.key === 'Space')) {
        if (lightbox?.elements) {
          const tabbableElements = [
            ...lightbox.elements.nav?.current?.querySelector('.fslightbox-toolbar')?.children,
            lightbox.elements.slideButtonNext?.current,
            lightbox.elements.slideButtonPrevious?.current
          ];

          if (tabbableElements.length > 0) {
            [...tabbableElements].forEach(element => {
              element.setAttribute('tabindex', '0');
              element.addEventListener('keyup', e => {
                e?.key && e.key === 'Enter' && element.click();
              });
            });
            tabbableElements[0].focus();
          }

          window.addEventListener('keydown', handleTabbing);
        }
      }
    };

    window.addEventListener('keyup', addKeyHandlers);
  };

  const popstateHandler = () => {
    props.setShowGallery(false);
    window.removeEventListener('popstate', popstateHandler);
  }

  return (
    <FsLightboxReact
      ref={lightbox}
      exitFullscreenOnClose={true}
      toggler={showGallery}
      sources={mediaItems}
      types={types}
      thumbs={thumbs}
      customAttributes={customAttributes}
      sourceIndex={galleryIndex}
      captions={captions.map((comment) => (
        <>
          <h4>{comment.memberId !== "" && comment.username !== "Anonymous User" ? comment.username : "Anonymous User"}</h4>
          <p>{comment.created}</p>
          <p>{comment.text}</p>
        </>
      ))}
      onOpen={lightbox => {
        trapFocusInLightbox(lightbox);
        window.history.pushState({}, '');
        window.addEventListener('popstate', popstateHandler);
      }}
      onClose={() => {
        window.removeEventListener('keydown', handleTabbing);
        window.removeEventListener('popstate', popstateHandler);
      }}
    />
  );
}

const mapStateToProps = (state) => {
  return {
    showGallery: selectors.showGallery(state),
    galleryIndex: selectors.galleryIndex(state),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setShowGallery: (showGallery) => dispatch(setShowGallery(showGallery)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(FsLightbox);
